import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "./ComplianceNotice.css";

/**
 * Regulatory notices that must be available at the point of use.
 *
 * `aiChat` and `aiGenerated` discharge EU AI Act Art. 50(1) and 50(2): a person must
 * be told they are interacting with an AI system, and AI-generated content must be
 * marked as such. Art. 50 has applied since 2 August 2026.
 *
 * `heartRate` and `poseAnalysis` are not AI Act obligations. They keep the product
 * outside the medical-device boundary by stating that the output is training
 * information rather than a diagnosis.
 *
 * Fallback strings are inlined so a missing translation degrades to English rather
 * than to a blank notice — an unrendered disclosure is a compliance gap.
 *
 * `mode="banner"` shows the notice inline (default for AI Act variants).
 * `mode="info"` shows a compact (i) control that reveals the text on demand
 * (used for heart-rate panels where the banner crowded the live UI).
 */

const VARIANTS = {
  aiChat: {
    Icon: SmartToyOutlinedIcon,
    key: "aiNoticeChat",
    fallback:
      "You are chatting with Digiping, an AI assistant. It can be wrong — your coach reviews and validates its suggestions.",
  },
  aiGenerated: {
    Icon: AutoAwesomeOutlinedIcon,
    key: "aiNoticeGenerated",
    fallback: "AI-generated. Review with your coach before acting on it.",
  },
  heartRate: {
    Icon: MonitorHeartOutlinedIcon,
    key: "aiNoticeHeartRate",
    fallback:
      "Heart-rate zones and intensity are training indicators, not medical advice. Consult a doctor for health decisions.",
  },
  poseAnalysis: {
    Icon: VideocamOutlinedIcon,
    key: "aiNoticePose",
    fallback:
      "Automated video analysis. Measurements are estimates and are not a diagnosis or an injury-risk assessment.",
  },
};

function ComplianceInfoButton({ text, className = "" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const tipId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span
      ref={rootRef}
      className={`compliance-notice-info ${className}`.trim()}
    >
      <button
        type="button"
        className="compliance-notice-info__btn"
        aria-label={t("moreInfo", "More information")}
        aria-expanded={open}
        aria-controls={tipId}
        title={text}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <InfoOutlinedIcon fontSize="inherit" aria-hidden />
      </button>
      {open ? (
        <span
          id={tipId}
          className="compliance-notice-info__tip"
          role="note"
        >
          {text}
        </span>
      ) : null}
    </span>
  );
}

export function ComplianceNotice({
  variant,
  compact = false,
  className = "",
  mode = "banner",
}) {
  const { t } = useTranslation();
  const config = VARIANTS[variant];
  if (!config) return null;

  const { Icon, key, fallback } = config;
  const text = t(key, fallback);

  if (mode === "info") {
    return <ComplianceInfoButton text={text} className={className} />;
  }

  return (
    <div
      className={`compliance-notice${compact ? " compliance-notice--compact" : ""} ${className}`.trim()}
      role="note"
    >
      <Icon className="compliance-notice__icon" fontSize="inherit" />
      <span className="compliance-notice__text">{text}</span>
    </div>
  );
}

export default ComplianceNotice;
